<template>
  <div class="license-status-email-delivery">
    <div class="section-title">Applicant email notifications</div>

    <div v-if="loading" class="small text-muted">Checking delivery status…</div>
    <div v-else-if="loadError" class="alert alert-warning py-2 px-3 mb-0 small" role="alert">
      Could not check email delivery status.
      <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="reloadDeliveries">
        Try again
      </button>
    </div>
    <div v-else-if="!entries.length" class="small text-muted">
      No applicant email notifications are configured.
    </div>

    <article v-for="entry in entries" :key="entry.rule.id" class="notification-delivery border rounded p-3">
      <div class="d-flex flex-row align-items-start justify-content-between gap-2">
        <div>
          <div class="fw-semibold">{{ entry.rule.name }}</div>
          <div class="small text-muted">{{ triggerDescription(entry.rule) }}</div>
        </div>
        <span :class="['status-badge', statusBadgeClass(entry)]">
          {{ statusLabel(entry) }}
        </span>
      </div>

      <div class="small text-muted">{{ statusMessage(entry) }}</div>
      <div v-if="entry.deliveryCount > 1" class="small text-muted">
        {{ entry.deliveryCount }} delivery records; showing the most recent.
      </div>

      <div class="d-flex flex-row flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="!entry.isConfigured"
          @click="$emit('preview', entry.rule)"
        >
          Preview
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="sendDisabled(entry)"
          :title="sendDisabledReason(entry)"
          @click="openSendConfirmation(entry)"
        >
          <span
            v-if="sendingRuleId === entry.rule.id"
            class="spinner-border spinner-border-sm me-1"
            aria-hidden="true"
          />
          {{ sendActionLabel(entry) }}
        </button>
      </div>

      <div v-if="!entry.isConfigured" class="small text-muted">
        This notification is no longer configured; its most recent delivery is retained for history.
      </div>
      <div v-else-if="entry.rule.mode === 'disabled'" class="small text-muted">
        This notification is disabled in city settings.
      </div>
      <div v-else-if="!entry.isStatusEligible" class="small text-muted">
        The license is not currently in this notification's trigger status.
      </div>
      <div v-if="entry.delivery.errorMessage" class="small text-danger">
        {{ entry.delivery.errorMessage }}
      </div>
    </article>

    <ModalView
      v-model="showSendConfirmation"
      :title="confirmationTitle"
      :subtitle="sendRecipientEmail"
      :ok-title="confirmationAction"
      :busy="Boolean(sendingRuleId)"
      @ok="queueEmail"
    >
      <p class="mb-0">
        Send <strong>{{ selectedEntry?.rule?.name }}</strong> to the applicant?
        <template v-if="selectedEntry?.delivery?.hasBeenSent">
          This will deliver another copy.
        </template>
      </p>
      <div v-if="sendError" class="alert alert-danger mt-3 mb-0" role="alert">
        {{ sendError.message || String(sendError) }}
      </div>
    </ModalView>
  </div>
</template>

<script>
import { toMoment } from '@/dateUtils';
import ModalView from '@/components/common/ModalView.vue';
import { License } from '@/models/License';

const ACTIVE_STATUSES = new Set(['pending', 'processing']);
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;

export default {
  components: { ModalView },
  emits: ['preview'],
  props: { license: License },
  data() {
    return {
      entries: [],
      loading: false,
      loadError: null,
      sendingRuleId: null,
      sendError: null,
      selectedEntry: null,
      showSendConfirmation: false,
      loadToken: 0,
      pollTimer: null,
      pollAttempts: 0,
      destroyed: false
    };
  },
  watch: {
    license: {
      immediate: true,
      handler() {
        this.reset();
        if (this.license) this.reloadDeliveries();
      }
    }
  },
  beforeUnmount() {
    this.destroyed = true;
    this.loadToken += 1;
    this.stopPolling();
  },
  computed: {
    sendRecipientEmail() {
      return String(this.license?.contact?.email || '').trim() || null;
    },
    hasActiveDelivery() {
      return this.entries.some(entry => ACTIVE_STATUSES.has(entry.delivery?.status));
    },
    confirmationTitle() {
      if (this.selectedEntry?.delivery?.status === 'error') return 'Retry email delivery?';
      return this.selectedEntry?.delivery?.hasBeenSent ? 'Send another email?' : 'Send email?';
    },
    confirmationAction() {
      if (this.selectedEntry?.delivery?.status === 'error') return 'Retry email';
      return this.selectedEntry?.delivery?.hasBeenSent ? 'Send again' : 'Send email';
    }
  },
  methods: {
    reset() {
      this.loadToken += 1;
      this.stopPolling();
      this.entries = [];
      this.loading = false;
      this.loadError = null;
      this.sendingRuleId = null;
      this.sendError = null;
      this.selectedEntry = null;
      this.showSendConfirmation = false;
    },
    async reloadDeliveries() {
      await this.loadDeliveries();
      if (this.hasActiveDelivery) this.startPolling();
    },
    async loadDeliveries({ silent = false } = {}) {
      const token = ++this.loadToken;
      if (!silent) {
        this.loading = true;
        this.loadError = null;
      }
      try {
        const entries = await this.license.getStatusEmailDeliveries();
        if (token === this.loadToken) {
          this.entries = entries;
          this.loadError = null;
        }
      } catch (error) {
        if (token === this.loadToken && !silent) this.loadError = error;
      } finally {
        if (token === this.loadToken && !silent) this.loading = false;
      }
    },
    triggerDescription(rule) {
      const status = this.license?.statusOptions?.find(option => option.code === rule.trigger?.statusCode);
      return `When entering ${status?.label || rule.trigger?.statusCode || 'the configured status'}`;
    },
    formatDate(value) {
      const timestamp = toMoment(value);
      return timestamp?.isValid() ? timestamp.format('MMM D, YYYY [at] h:mm a') : null;
    },
    statusLabel(entry) {
      switch (entry.delivery?.status) {
        case 'pending': return 'Queued';
        case 'processing': return 'Sending';
        case 'sent': return 'Sent';
        case 'error': return 'Failed';
        case 'skipped_by_staff': return 'Skipped';
        case 'missing_recipient': return 'No recipient';
        default: return 'Not sent';
      }
    },
    statusBadgeClass(entry) {
      switch (entry.delivery?.status) {
        case 'sent': return 'status-badge--success';
        case 'pending':
        case 'processing': return 'status-badge--pending';
        case 'error': return 'status-badge--error';
        default: return 'status-badge--neutral';
      }
    },
    statusMessage(entry) {
      const delivery = entry.delivery || {};
      const recipient = delivery.recipientEmail ? ` to ${delivery.recipientEmail}` : '';
      const sentAt = this.formatDate(delivery.sentAt);
      if (delivery.status === 'sent') return `Sent${sentAt ? ` ${sentAt}` : ''}${recipient}.`;
      if (delivery.status === 'pending') return `Queued${recipient}.`;
      if (delivery.status === 'processing') return `Sending${recipient}.`;
      if (delivery.status === 'error') return delivery.hasBeenSent
        ? `Last sent ${sentAt}; the latest attempt failed.`
        : 'The latest delivery attempt failed.';
      if (delivery.status === 'skipped_by_staff') return 'Staff chose not to send this notification.';
      if (delivery.status === 'missing_recipient') return 'No applicant email was available at the transition.';
      return 'No delivery has been recorded for this notification.';
    },
    sendDisabled(entry) {
      return Boolean(this.sendingRuleId)
        || ACTIVE_STATUSES.has(entry.delivery?.status)
        || !this.sendRecipientEmail
        || !entry.isConfigured
        || entry.rule.mode === 'disabled'
        || !entry.isStatusEligible;
    },
    sendDisabledReason(entry) {
      if (this.sendRecipientEmail === null) return 'The applicant does not have an email address';
      if (!entry.isConfigured) return 'This notification is no longer configured';
      if (entry.rule.mode === 'disabled') return 'This notification is disabled';
      if (!entry.isStatusEligible) return 'The license is not in the trigger status';
      return null;
    },
    sendActionLabel(entry) {
      if (this.sendingRuleId === entry.rule.id) return 'Queueing…';
      if (entry.delivery?.status === 'processing') return 'Sending…';
      if (entry.delivery?.status === 'pending') return 'Email queued';
      if (entry.delivery?.status === 'error') return 'Retry';
      return entry.delivery?.hasBeenSent ? 'Send again' : 'Send email';
    },
    openSendConfirmation(entry) {
      if (this.sendDisabled(entry)) return;
      this.selectedEntry = entry;
      this.sendError = null;
      this.showSendConfirmation = true;
    },
    async queueEmail() {
      const entry = this.selectedEntry;
      if (!entry || this.sendDisabled(entry)) return;
      this.sendingRuleId = entry.rule.id;
      this.sendError = null;
      this.loadToken += 1;
      try {
        const result = await this.license.sendStatusEmail(entry.rule.id);
        entry.delivery = result?.delivery || entry.delivery;
        this.showSendConfirmation = false;
        this.$showToast(
          result?.outcome === 'already_queued'
            ? `${entry.rule.name} is already queued.`
            : `${entry.rule.name} queued for ${this.sendRecipientEmail}.`,
          { type: 'primary' }
        );
        if (this.hasActiveDelivery) this.startPolling();
      } catch (error) {
        this.sendError = error;
      } finally {
        this.sendingRuleId = null;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollAttempts = 0;
      this.schedulePoll();
    },
    schedulePoll() {
      if (this.destroyed || !this.hasActiveDelivery || this.pollAttempts >= MAX_POLL_ATTEMPTS) return;
      this.pollTimer = setTimeout(() => this.pollDeliveries(), POLL_INTERVAL_MS);
    },
    async pollDeliveries() {
      this.pollTimer = null;
      this.pollAttempts += 1;
      await this.loadDeliveries({ silent: true });
      this.schedulePoll();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.license-status-email-delivery {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notification-delivery {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.section-title {
  color: var(--bs-secondary-color);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge {
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  white-space: nowrap;
}

.status-badge--success { color: var(--bs-success-text-emphasis); background: var(--bs-success-bg-subtle); }
.status-badge--pending { color: var(--bs-primary-text-emphasis); background: var(--bs-primary-bg-subtle); }
.status-badge--error { color: var(--bs-danger-text-emphasis); background: var(--bs-danger-bg-subtle); }
.status-badge--neutral { color: var(--bs-secondary-color); background: var(--bs-secondary-bg); }
</style>
